import React, { useEffect, useState, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ArrowDropDownIcon, ArrowDropUpIcon, Autocomplete, Box, Button, Chip, ClearIcon, DragHandleIcon, Grid, IconButton, InfoIcon, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "../../styles/MaterialUI";
import apiConstants from "../../constants/apiConstants";
import axios from "axios";
import { useSelector } from "react-redux";
import { buildContentTypeOptions } from "../../utils/contentTypeUtils";
import ToolTip from '../../utils/CursorTooltip'
import { useOrgConfigData } from "../../hooks/useOrgConfigData";

const ContentPlaylist = ({ addPlaylist, showError, modifyPlaylist = [], maxContentPerPlaylist }) => {
  const { userToken } = useSelector((state) => state.userToken);

  const orgConfig = useOrgConfigData("mapScreen");
  const {
    org_properties: org_configurations,
    all_content_types,
    permitted_content_types
  } = orgConfig || {};
  const headers = { Authorization: `Bearer ${userToken}` };
  const [contentList, setContentList] = useState([]);
  const [allContent, setAllContent] = useState([]);
  const [orgIntegrationData, setOrgIntegrationData] = useState([]);

  const allContentTypes = all_content_types ?? [];
  const permittedContentTypes = permitted_content_types ?? [];

  // Maps the visible content-type label to the internal cType value used in ContentPlaylist.
  // "Content Playlist" intentionally excluded — can't nest playlists.
  const PLAYLIST_TYPE_MAP = {
    "Image": "image",
    "Video": "video",
    "IntelliDash Dashboard": "dashboard",
    "Machine Metrics": "metrics_dashboard",
    "Acumatica": "acumatica",
    "Power BI": "power_bi",
    "Open URL": "open_url",
  };

  const getNormalizedPlaylistType = (contentData, integration) => {
    const rawType = contentData?.ic_content_type;
    const integrationName = integration?.integration_name;

    if (["image", "video", "dashboard", "content_playlist", "open_url"].includes(rawType)) {
      return rawType;
    }

    if (rawType === "orchestrated_url") {
      if (integrationName === "Machine Metrics") return "metrics_dashboard";
      if (integrationName === "Acumatica") return "acumatica";
      if (integrationName === "Power BI") return "power_bi";
    }

    return rawType ?? "image";
  };

  const contentTypeOptions = useMemo(() => {
    const options = buildContentTypeOptions(
      allContentTypes,
      permittedContentTypes,
      orgIntegrationData
    );

    // const getId = (id) =>
    //   id ? Number(id.replace(/\D/g, "")) : 9999;

    return [...options]
      .filter((o) => PLAYLIST_TYPE_MAP[o.label] !== undefined)
      .sort((a, b) => {
        // 1. enabled first
        // if (a.disabled !== b.disabled) {
        //   return a.disabled ? 1 : -1;
        // }

        // 2. then ID
        return a.ic_content_type_name - b.ic_content_type_name;
      });
  }, [allContentTypes, permittedContentTypes, orgIntegrationData]);


  useEffect(() => {
    addPlaylist(contentList);
  }, [contentList]);

  const getIntegrationsData = async () => {
    try {
      const apiResponse = await axios.post(`${apiConstants.GET_API_URL}getIntegrations`, {}, { headers });
      const { data } = apiResponse.data;
      setOrgIntegrationData(data);
      return data;
    } catch (error) {
      const { statusCode, message, redirect_url } = error.response.data;
      showError(message);
      if (statusCode === 403) window.open(redirect_url, "_self");
    }
  };

  useEffect(() => {
    const init = async () => {
      const integrations = await getIntegrationsData();
      await handleEditContentPlaylist(modifyPlaylist, integrations);
    };
    init();
  }, []);

  const handleEditContentPlaylist = async (playlist, integrations) => {
    try {
      const settingsMap = { 1: "Play Once", 2: "Play Twice", 3: "Play Thrice" };

      const response = await axios.post(
        `${apiConstants.GET_API_URL}getContents`,
        { org_id: "test" },
        { headers }
      );
      const { data } = response.data;

      data.forEach(item => {
        item.ic_content_status = item.ic_content_status === 1 ? "Enabled" : "Disabled";
      });

      if (playlist && playlist.length > 0) {
        const editContentList = playlist.map(content => {
          const matchedContent = data.find(d => d.ic_content_id == content.content_id);
          if (!matchedContent) return content;

          const uniqueId = Math.random().toString(36).substring(2, 9);

          const integration = integrations.find(
            item => item.ic_org_integration_id == matchedContent.ic_org_integration_id
          );

          const matched_content_type = getNormalizedPlaylistType(matchedContent, integration);

          let contentNameList;
          if (integration) {
            contentNameList = data
              .filter(item => item.ic_org_integration_id == integration.ic_org_integration_id)
              .map(c => c.ic_content_name)
              .sort((a, b) => a.localeCompare(b));
          } else if (matchedContent.ic_content_type === "dashboard") {
            contentNameList = data
              .filter(item => item.ic_content_type === "dashboard" && item.ic_org_integration_id == null)
              .map(c => c.ic_content_name)
              .sort((a, b) => a.localeCompare(b));
          } else {
            contentNameList = data
              .filter(item => item.ic_content_type === matchedContent.ic_content_type)
              .map(c => c.ic_content_name)
              .sort((a, b) => a.localeCompare(b));
          }

          return {
            ...content,
            cId: uniqueId,
            cType: matched_content_type,
            content_Id: content.content_id,
            cName: matchedContent.ic_content_name,
            cStatus: matchedContent.ic_content_status,
            cSettings:
              matchedContent.ic_content_type === "video"
                ? settingsMap[content.display_setting]
                : content.display_setting,
            cSettingsList:
              matchedContent.ic_content_type === "video"
                ? ["Play Once", "Play Twice", "Play Thrice"]
                : [],
            cNameList: contentNameList,
          };
        }).sort((a, b) => a.content_sequence - b.content_sequence);

        setContentList(editContentList);
      } else {
        setContentList([]);
      }

      setAllContent(data);
    } catch (error) {
      const { statusCode, message, redirect_url } = error.response.data;
      showError(message);
      if (statusCode === 403) window.open(redirect_url, "_self");
    }
  };

  const handleDragEnd = (e) => {
    if (!e.destination) return;
    const tempData = Array.from(contentList);
    const [selectedContent] = tempData.splice(e.source.index, 1);
    tempData.splice(e.destination.index, 0, selectedContent);
    setContentList(tempData);
  };

  const handleAddRow = async () => {
    if (contentList.length >= maxContentPerPlaylist) {
      const errorMessage =
        maxContentPerPlaylist < 20
          ? "You have reached the maximum content limit for this playlist. Upgrade to add more contents to the playlist."
          : "Cannot add more than 20 contents to the playlist.";
      showError(errorMessage);
      return;
    }

    const randNum = Math.random().toString(36).substring(2, 2 + 7);
    try {
      const response = await axios.post(`${apiConstants.GET_API_URL}getContents`, { org_id: "test" }, { headers });
      const { data } = response.data;

      data.forEach(item => {
        item.ic_content_status = item.ic_content_status === 1 ? "Enabled" : "Disabled";
      });

      const contentNames = data
        .filter(content => content.ic_content_type === "image")
        .map(content => content.ic_content_name)
        .sort((a, b) => a.localeCompare(b));

      const newRow = {
        cId: randNum, cType: "image", cNameList: contentNames,
        content_Id: null, cName: "", cSettingsList: [], cSettings: 5, cStatus: "", created_by: "",
      };

      setContentList(prev => [...prev, newRow]);
      setAllContent(data);
    } catch (error) {
      const { statusCode, message, redirect_url } = error.response.data;
      showError(message);
      if (statusCode === 403) window.open(redirect_url, "_self");
    }
  };

  const handleContentTypeChange = async (e, contentId) => {
    const { value } = e.target;
    const videoLength = contentList.filter(content => content.cType === "video").length;

    if (value === "video" && videoLength >= 5) {
      showError("Cannot add more than 5 video contents to the playlist.");
      return;
    }

    try {
      const response = await axios.post(`${apiConstants.GET_API_URL}getContents`, { org_id: "test" }, { headers });
      const { data } = response.data;

      data.forEach(item => {
        item.ic_content_status = item.ic_content_status === 1 ? "Enabled" : "Disabled";
      });

      //  renamed from `contentList` to avoid shadowing the state variable
      let filteredNameList;

      if (value === "metrics_dashboard") {
        const id = getIntegrationId("Machine Metrics");
        filteredNameList = data
          .filter(item => item.ic_org_integration_id === id)
          .map(c => c.ic_content_name)
          .sort((a, b) => a.localeCompare(b));
      } else if (value === "acumatica") {
        const id = getIntegrationId("Acumatica");
        filteredNameList = data
          .filter(item => item.ic_org_integration_id === id)
          .map(c => c.ic_content_name)
          .sort((a, b) => a.localeCompare(b));
      } else if (value === "power_bi") {
        const id = getIntegrationId("Power BI");
        filteredNameList = data
          .filter(item => item.ic_org_integration_id === id)
          .map(c => c.ic_content_name)
          .sort((a, b) => a.localeCompare(b));
      } else {
        filteredNameList = data
          .filter(({ ic_content_type, ic_org_integration_id }) =>
            ic_content_type === value && ic_org_integration_id === null
          )
          .map(content => content.ic_content_name)
          .sort((a, b) => a.localeCompare(b));
      }

      //  setContentList now correctly refers to the state setter
      setContentList(prevContent =>
        prevContent.map(content => {
          if (content.cId === contentId) {
            return {
              ...content,
              cType: value,
              created_by: "",
              ...(value !== "video"
                ? { cSettings: 5, cSettingsList: [], cNameList: filteredNameList, content_Id: null, cName: "", cStatus: "" }
                : { cSettings: "Play Once", cSettingsList: ["Play Once", "Play Twice", "Play Thrice"], cNameList: filteredNameList, content_Id: null, cName: "", cStatus: "" }
              ),
            };
          }
          return content;
        })
      );

      setAllContent(data);
    } catch (error) {
      const { statusCode, message, redirect_url } = error.response.data;
      showError(message);
      if (statusCode === 403) window.open(redirect_url, "_self");
    }
  };

  const handleVideoSettingsChange = (e, contentId) => {
    const { value } = e.target;
    setContentList(prevContent =>
      prevContent.map(content => content.cId === contentId ? { ...content, cSettings: value } : content)
    );
  };

  const handleSettingChange = (contentId, sKey, contentType) => {
    if (contentType === "metrics_dashboard" && !isIntegrationActive("Machine Metrics")) {
      showError("Please enable the integration flag");
      return;
    }
    if (contentType === "acumatica" && !isIntegrationActive("Acumatica")) {
      showError("Please enable the integration flag");
      return;
    }
    if (contentType === "power_bi" && !isIntegrationActive("Power BI")) {
      showError("Please enable the integration flag");
      return;
    }

    const adjustSettings = (currentSettings, key) => {
      if (key === "up") {
        return currentSettings === 0.5 ? 1 : currentSettings === 1 ? 5 : currentSettings < 60 ? currentSettings + 5 : currentSettings;
      } else {
        return currentSettings === 5 ? 1 : currentSettings === 1 ? 0.5 : currentSettings >= 10 ? currentSettings - 5 : currentSettings;
      }
    };

    setContentList(prevContent =>
      prevContent.map(content =>
        content.cId === contentId ? { ...content, cSettings: adjustSettings(content.cSettings, sKey) } : content
      )
    );
  };

  const handleContentNameChange = (value, contentId) => {
    const matchedContent = value && allContent?.find(content => content.ic_content_name === value);
    const { ic_content_id = null, ic_content_status = "", ic_createdby = "" } = matchedContent || {};
    setContentList(prevContent =>
      prevContent.map(content =>
        content.cId === contentId
          ? { ...content, content_Id: ic_content_id, cName: value || "", cStatus: ic_content_status, created_by: ic_createdby }
          : content
      )
    );
  };

  const handleDeleteContent = (contentId, contentType) => {
    if (contentType === "metrics_dashboard" && !isIntegrationActive("Machine Metrics")) {
      showError("Please enable the integration flag");
      return;
    }
    if (contentType === "acumatica" && !isIntegrationActive("Acumatica")) {
      showError("Please enable the integration flag");
      return;
    }
    if (contentType === "power_bi" && !isIntegrationActive("Power BI")) {
      showError("Please enable the integration flag");
      return;
    }
    setContentList(prevContent => prevContent.filter(content => content.cId !== contentId));
  };

  const getIntegrationByName = (name) =>
    orgIntegrationData.find(item => item.integration_name === name);

  const isIntegrationActive = (name) =>
    orgIntegrationData.some(
      item => item.integration_name === name && item.ic_activeflag === 1 && item.ic_integration_flag === 1
    );

  const getIntegrationById = (id) =>
    orgIntegrationData.find(item => item.ic_org_integration_id === id);

  const getIntegrationId = (name) => getIntegrationByName(name)?.ic_org_integration_id;

  return (
    <DragDropContext onDragEnd={(e) => handleDragEnd(e)}>
      <Grid item xs={12} sm={6}>
        <Button variant="outlined" style={{ textTransform: "none", color: "#003366" }} onClick={handleAddRow}>
          Add Content to Playlist
        </Button>
      </Grid>
      <Grid item xs={12} sm={12}>
        <TableContainer component={Paper} sx={{ maxHeight: "420px", scrollbarWidth: "thin" }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#003366", width: "100%", textWrap: "wrap", position: "sticky", top: 0, zIndex: 1 }}>
              <TableRow>
                <TableCell sx={{ color: "white" }}>Actions</TableCell>
                <TableCell sx={{ color: "white" }}>Content Type</TableCell>
                <TableCell sx={{ color: "white" }}>Content Name</TableCell>
                <TableCell sx={{ color: "white" }}>Display Settings</TableCell>
                <TableCell sx={{ color: "white" }}>Status</TableCell>
                <TableCell sx={{ color: "white" }}>Created By</TableCell>
              </TableRow>
            </TableHead>
            <Droppable droppableId="tbody-1">
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {contentList?.map((content, index) => (
                    <Draggable key={content.cId} draggableId={content.cId} index={index}>
                      {(provided) => (
                        <TableRow ref={provided.innerRef} {...provided.draggableProps}>
                          <TableCell {...provided.dragHandleProps}>
                            <Box sx={{ display: "flex" }}>
                              <Tooltip title="Click & Drag to reorder">
                                <IconButton color="info" sx={{ cursor: "grab" }}>
                                  <DragHandleIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove">
                                <IconButton color="error" size="small" sx={{ cursor: "pointer" }} onClick={() => handleDeleteContent(content.cId, content.cType)}>
                                  <ClearIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap", minWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>
                            <Autocomplete
                              size="small"
                              value={contentTypeOptions.find((opt) => PLAYLIST_TYPE_MAP[opt.label] === content.cType) ?? null}
                              options={contentTypeOptions}
                              getOptionLabel={(option) => option?.label ?? ""}
                              getOptionDisabled={(option) => option.disabled}
                              isOptionEqualToValue={(option, value) => option.label === value?.label}
                              onChange={(e, newValue) =>
                                handleContentTypeChange(
                                  { target: { value: PLAYLIST_TYPE_MAP[newValue?.label] } },
                                  content.cId
                                )
                              }
                              sx={{
                                fontSize: "0.9rem",
                                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                              }}
                              renderOption={(props, option, { index }) => {
                                const isIntegrationType = ["Machine Metrics", "Acumatica", "Power BI"].includes(option.label);
                                const integrationInactive = isIntegrationType && !isIntegrationActive(option.label);

                                const tooltipText = option.disabled
                                  ? integrationInactive
                                    ? "Integration not configured or inactive"
                                    : "Upgrade to use this content type"
                                  : "";

                                // Show a divider right before the first disabled option in the list
                                const isFirstDisabled =
                                  option.disabled &&
                                  (index === 0 || !contentTypeOptions[index - 1]?.disabled);

                                return (
                                  <React.Fragment key={option.label}>
                                    {isFirstDisabled && (
                                      <div style={{ borderTop: "0.5px solid #afa1a1", margin: "2px 0" }} />
                                    )}
                                    <ToolTip title={tooltipText} disableHoverListener={!option.disabled}>
                                      <span style={{ display: "block", width: "100%" }}>
                                        <li
                                          {...props}
                                          aria-disabled={option.disabled}
                                          style={{
                                            ...props.style,
                                            color: option.disabled ? "#574d4d" : "inherit",
                                            cursor: option.disabled ? "not-allowed" : "pointer",
                                            pointerEvents: option.disabled ? "none" : "auto",
                                            opacity: option.disabled ? 0.6 : 1,
                                          }}
                                        >
                                          {option.label}
                                        </li>
                                      </span>
                                    </ToolTip>
                                  </React.Fragment>
                                );
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                />
                              )}
                            />
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap", minWidth: "250px", overflow: "hidden", textOverflow: "ellipsis" }}>
                            <Tooltip title={content.cName.length > 15 && content.cName}>
                              <Autocomplete
                                size="small"
                                options={content.cNameList}
                                value={content.cName}
                                disabled={
                                  (content.cType === "metrics_dashboard" && !isIntegrationActive("Machine Metrics")) ||
                                  (content.cType === "acumatica" && !isIntegrationActive("Acumatica")) ||
                                  (content.cType === "power_bi" && !isIntegrationActive("Power BI"))
                                }
                                onChange={(e, value) => handleContentNameChange(value, content.cId)}
                                sx={{ '& .MuiAutocomplete-input': { fontSize: "0.9rem" } }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        '& fieldset': { border: 'none' },
                                      },
                                    }}
                                  />
                                )}
                                ListboxProps={{ sx: { scrollbarWidth: "thin" } }}
                              />
                            </Tooltip>
                          </TableCell>

                          <TableCell>
                            {content.cType !== "video" ? (
                              <Stack spacing={-0.5} alignItems="center">
                                <ArrowDropUpIcon onClick={() => handleSettingChange(content.cId, "up", content.cType)} sx={{ cursor: "pointer", color: "#0288d1" }} />
                                <span>{`${content.cSettings} ${content.cSettings > 1 ? "Mins" : "Min"}`}</span>
                                <ArrowDropDownIcon onClick={() => handleSettingChange(content.cId, "down", content.cType)} sx={{ cursor: "pointer", color: "#0288d1" }} />
                              </Stack>
                            ) : (
                              <Select
                                value={content.cSettings}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                  fontSize: "0.9rem",
                                }}
                                onChange={(e) => handleVideoSettingsChange(e, content.cId)}
                              >
                                {content.cSettingsList.map((setting, i) => (
                                  <MenuItem key={i} value={setting}>{setting}</MenuItem>
                                ))}
                              </Select>
                            )}
                          </TableCell>

                          <TableCell>
                            {content.cStatus && (
                              <Stack direction="row" display="flex" alignItems="center">
                                <Chip
                                  label={content.cStatus}
                                  size="small"
                                  sx={{
                                    backgroundColor: content.cStatus === "Enabled" ? "green" : content.cStatus === "Disabled" ? "red" : "#ffcb7f",
                                    width: "100px", color: "#fff",
                                  }}
                                />
                                {content.cStatus === "Disabled" && (
                                  <Tooltip title="Contents in Disabled status cannot be displayed when the content playlist is mapped to the screens" arrow>
                                    <InfoIcon color="info" sx={{ fontSize: "20px", mr: 0.5 }} />
                                  </Tooltip>
                                )}
                              </Stack>
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography sx={{ fontSize: "0.9rem" }}>{content.created_by}</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </TableContainer>
      </Grid>
    </DragDropContext>
  );
};

export default ContentPlaylist;